import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { from } from 'rxjs';
import { take } from 'rxjs/operators';

import { GlobalConstants } from 'app/shared/constants';

import { BootstrapService } from './bootstrap.service';
import { ErrorMessageService } from './error-message.service';
import { MessageService } from './message.service';
import { OAuthService } from './oauth.service';
import { StorageService } from './storage.service';

@Injectable({ providedIn: 'root' })
export class AuthRestartService {
    private readonly bootstrap = inject(BootstrapService);
    private readonly errorMessageService = inject(ErrorMessageService);
    private readonly messageService = inject(MessageService);
    private readonly oauthService = inject(OAuthService);
    private readonly router = inject(Router);
    private readonly storage = inject(StorageService);

    restartAuthorization(options?: AuthRestartOptions): void {
        const returnUrl = options?.returnUrl ?? this.resolveReturnUrl();

        this.bootstrap.reset();
        this.bootstrap.markLoading();
        this.clearSessionState();

        if (options?.notify) {
            from(this.errorMessageService.resolveSpaError('reset-flow'))
                .pipe(take(1))
                .subscribe((sessionMsg: string) => {
                    this.messageService.warning(sessionMsg);
                    this.oauthService.startAuthorizationFlow(returnUrl);
                });
            return;
        }

        this.oauthService.startAuthorizationFlow(returnUrl);
    }

    private clearSessionState(): void {
        this.storage.remove(GlobalConstants.CURRENT_USER_KEY);
        this.storage.remove(GlobalConstants.OAUTH_BEARER_KEY);
        this.storage.remove(GlobalConstants.OAUTH_STATE_KEY);
        this.storage.remove(GlobalConstants.PKCE_VERIFIER_KEY);
        this.storage.remove(GlobalConstants.POST_LOGIN_URL_KEY);
    }

    private resolveReturnUrl(): string {
        if (this.router.url) {
            return this.router.url;
        }

        return `${window.location.pathname}${window.location.search}${window.location.hash}`;
    }
}

interface AuthRestartOptions {
    notify?: boolean;
    returnUrl?: string;
}
